// group.js
// Full group ratchet (MLS, RFC 9420) instead of pairwise fan-out. One
// evolving shared group key, efficient at any group size, proper forward
// secrecy AND post-compromise security on membership changes (a removed
// member can't read future messages; a compromised member's key gets
// rotated out on their next commit).
//
// Wraps ts-mls's lower-level API into the same shape as the 1:1 chat.js:
// generate a key package, create/join a group, send/receive application
// messages. The Authentication Service (see group-trust.js) reuses our
// existing long-term Ed25519 identity as the credential — the same
// identity and fingerprint already used for 1:1 chat.

import {
  createApplicationMessage,
  createCommit,
  createGroup as mlsCreateGroup,
  joinGroup as mlsJoinGroup,
  processMessage,
  generateKeyPackageWithKey,
  getCiphersuiteImpl,
  getCiphersuiteFromName,
  encodeMlsMessage,
  decodeMlsMessage,
  encodeGroupState,
  decodeGroupState,
  defaultCapabilities,
  defaultLifetime,
  defaultKeyRetentionConfig,
  defaultLifetimeConfig,
  defaultKeyPackageEqualityConfig,
  defaultPaddingConfig,
  makePskIndex,
  acceptAll,
  zeroOutUint8Array
} from 'ts-mls'

// Zero extra dependencies for this suite (X25519 + AES128GCM + Ed25519) —
// matches the curves we already use elsewhere in the project.
export const CIPHERSUITE = 'MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519'

// decodeMlsMessage returns [value, bytesConsumed] | undefined (a low-level
// TLS-style decoder) rather than just the value — this unwraps it.
function decodeFull (bytes) {
  const result = decodeMlsMessage(bytes, 0)
  return result ? result[0] : undefined
}

export async function makeMlsContext (trustStore, onNewIdentity) {
  const cipherSuite = await getCiphersuiteImpl(getCiphersuiteFromName(CIPHERSUITE))
  const authService = trustStore.toAuthService(onNewIdentity)
  const clientConfig = {
    keyRetentionConfig: defaultKeyRetentionConfig,
    lifetimeConfig: defaultLifetimeConfig,
    keyPackageEqualityConfig: defaultKeyPackageEqualityConfig,
    paddingConfig: defaultPaddingConfig,
    authService
  }
  return { cipherSuite, clientConfig }
}

/**
 * Generate this peer's MLS key package. Unlike a typical MLS deployment
 * (which mints a fresh ephemeral signing key per key package for
 * unlinkability), this reuses our persistent Ed25519 identity key
 * (identity.js) as the leaf's actual signing key. That means MLS's own
 * signature verification cryptographically proves group membership belongs
 * to the holder of the pinned identity — not just "same bytes we saw
 * before" (TOFU on an unrelated ephemeral key). Trade-off: leaf identity is
 * linkable across groups/sessions — acceptable here since contacts are
 * already meant to recognize the same identity everywhere (1:1 chat and
 * groups share one trust store, see group-trust.js).
 */
export async function generateOwnKeyPackage (identity, ctx) {
  const credential = {
    credentialType: 'basic',
    identity: identity.publicKey.raw
  }
  // identity.privateKey.raw is libp2p's 64-byte (seed || pub) encoding;
  // the underlying Ed25519 signing primitive wants just the 32-byte seed.
  const rawPriv = identity.privateKey.raw
  const signKey = rawPriv.length === 64 ? rawPriv.slice(0, 32) : rawPriv
  const signatureKeyPair = { signKey, publicKey: identity.publicKey.raw }
  return generateKeyPackageWithKey(credential, defaultCapabilities(), defaultLifetime, [], signatureKeyPair, ctx.cipherSuite)
}

export function encodeKeyPackage (keyPackage) {
  return encodeMlsMessage({ version: 'mls10', wireformat: 'mls_key_package', keyPackage })
}

export function decodeKeyPackage (bytes) {
  const decoded = decodeFull(bytes)
  if (!decoded || decoded.wireformat !== 'mls_key_package') {
    throw new Error('Expected an MLS key package')
  }
  return decoded.keyPackage
}

/** Pull the credential identity bytes (our long-term Ed25519 pubkey) out of a key package. */
export function identityFromKeyPackage (keyPackage) {
  return keyPackage.leafNode.credential.identity
}

/** Create a brand new group with just yourself as the founding member. */
export async function createGroup (ctx, groupIdStr, ownKeyPackage) {
  const groupId = new TextEncoder().encode(groupIdStr)
  return mlsCreateGroup(groupId, ownKeyPackage.publicPackage, ownKeyPackage.privatePackage, [], ctx.cipherSuite, ctx.clientConfig)
}

/**
 * Add one or more members (by their encoded key packages) to the group.
 * Returns { newState, welcomeBytes, commitBytes } — send commitBytes to
 * existing members, welcomeBytes to the new member(s).
 */
export async function addMembers (ctx, state, encodedKeyPackages) {
  const proposals = encodedKeyPackages.map((bytes) => ({
    proposalType: 'add',
    add: { keyPackage: decodeKeyPackage(bytes) }
  }))

  const result = await createCommit(
    { state, cipherSuite: ctx.cipherSuite, pskIndex: makePskIndex(state, {}) },
    {
      extraProposals: proposals,
      // Embed the ratchet tree in the welcome's GroupInfo extension so a
      // new member joining over the network gets it as part of the welcome
      // bytes, without us needing a separate out-of-band tree transfer.
      ratchetTreeExtension: true
    }
  )
  result.consumed.forEach(zeroOutUint8Array)

  return {
    newState: result.newState,
    welcomeBytes: result.welcome
      ? encodeMlsMessage({ version: 'mls10', wireformat: 'mls_welcome', welcome: result.welcome })
      : null,
    commitBytes: encodeMlsMessage(result.commit)
  }
}

/** Join a group as a new member, using the welcome message the adder sent you. */
export async function joinGroup (ctx, welcomeBytes, ownKeyPackage, ratchetTree) {
  const decoded = decodeFull(welcomeBytes)
  if (!decoded || decoded.wireformat !== 'mls_welcome') {
    throw new Error('Expected an MLS welcome message')
  }
  return mlsJoinGroup(
    decoded.welcome,
    ownKeyPackage.publicPackage,
    ownKeyPackage.privatePackage,
    makePskIndex(undefined, {}),
    ctx.cipherSuite,
    ratchetTree,
    undefined,
    ctx.clientConfig
  )
}

/** Seal a plaintext application message for the current group state. */
export async function sendMessage (ctx, state, plaintext) {
  const result = await createApplicationMessage(state, new TextEncoder().encode(plaintext), ctx.cipherSuite)
  result.consumed.forEach(zeroOutUint8Array)
  return {
    newState: result.newState,
    encoded: encodeMlsMessage({ version: 'mls10', wireformat: 'mls_private_message', privateMessage: result.privateMessage })
  }
}

/**
 * Process an incoming message (application message OR a commit updating
 * group membership). Returns { newState, plaintext? } — plaintext is only
 * set for application messages; commits just advance the state.
 */
export async function receiveMessage (ctx, state, encodedBytes) {
  const decoded = decodeFull(encodedBytes)
  if (!decoded) throw new Error('Failed to decode MLS message')
  if (decoded.wireformat !== 'mls_private_message' && decoded.wireformat !== 'mls_public_message') {
    throw new Error(`Cannot process wireformat ${decoded.wireformat} as an incoming group message`)
  }

  const result = await processMessage(decoded, state, makePskIndex(state, {}), acceptAll, ctx.cipherSuite)
  result.consumed?.forEach(zeroOutUint8Array)

  if (result.kind === 'newState') {
    return { newState: result.newState }
  }
  return { newState: result.newState, plaintext: new TextDecoder().decode(result.message) }
}

/**
 * Serialize a group's complete MLS state (ratchet tree, secret tree, key
 * schedule, our private path and signing key, retained receiver keys) so it
 * can be stored and later restored. The bytes contain private key material:
 * treat them like the identity key and encrypt them at rest.
 */
export function exportGroupState (state) {
  return encodeGroupState(state)
}

/**
 * Restore a state saved by exportGroupState. The codec deliberately leaves
 * clientConfig out (it holds functions — the authentication service, key
 * retention rules — that can't be serialized), so the live config from this
 * run's MLS context is attached. A restored state without it fails the first
 * time it is used.
 */
export function importGroupState (ctx, bytes) {
  // Decode from a private copy. The decoder returns views into the buffer it is
  // given, and ts-mls zeroes consumed key material in place, so using a
  // restored state would otherwise overwrite parts of the very bytes it was
  // restored from, corrupting any later restore from the same buffer.
  const decoded = decodeGroupState(new Uint8Array(bytes), 0)
  const state = Array.isArray(decoded) ? decoded[0] : decoded
  if (!state) throw new Error('Could not decode saved group state')
  return { ...state, clientConfig: ctx.clientConfig }
}
